import React from 'react';
import { format, parse } from 'date-fns';
import * as yup from 'yup';

import Button from '@hero/ui-kit/inputs/Button';
import Form from '@hero/ui-kit/inputs/Form';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';

import { useLockMonth } from '../api/useCareWrites';
import { asRefusal, careErrorText } from '../constants';
import * as Style from '../style.module.scss';
import FailedChecks from './FailedChecks';

const schema = yup.object({ reason: yup.string().trim().required('Enter a reason.') });

const LockForm: React.FC<{ month: string; closeModal: () => void; canWrite: boolean }> = ({
    month,
    closeModal,
    canWrite
}) => {
    const { mutateAsync, isPending, error } = useLockMonth(month);
    const refusal = asRefusal(error)?.data;

    return (
        <Section>
            <H role="h5">{`Lock ${format(parse(month, 'yyyy-MM', new Date()), 'MMMM yyyy')}`}</H>
            <P>
                After the lock nothing can change this month. Only Ops can unlock it, before the
                first claim.
            </P>
            <Form
                validationSchema={schema}
                submitFn={(values: { reason: string }) =>
                    (canWrite ? mutateAsync(values) : Promise.reject())
                        .then(closeModal)
                        .catch(() => {})
                }
                validationMode="onTouched"
            >
                <TextArea name="reason" displayName="Reason" disabled={!canWrite || isPending} />
                {refusal?.checks?.length ? (
                    <Section noDefaultPadding paddingBottom="small">
                        <P strong>This month is not ready to lock:</P>
                        <FailedChecks checks={refusal.checks} />
                    </Section>
                ) : error ? (
                    <P className={Style.apiError} noDefaultMargin>
                        {careErrorText(error)}
                    </P>
                ) : null}
                <Section noDefaultPadding paddingTop="small" centered>
                    <Button type="submit" variant="primary" disabled={!canWrite || isPending}>
                        Lock month
                    </Button>
                </Section>
            </Form>
        </Section>
    );
};

export default LockForm;
